import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  type DensityName,
  type ThemeName,
  type TenantThemeOverride,
  buildCssVars,
  densityPresets,
  mergeTokenOverrides,
  themes,
} from "../theme/tokens";
import { sanitizeCssColor } from "../utils/cssColor";

// Feature flag — tenant overrides are only applied when this env var is "true"
const TENANT_THEMING_ENABLED =
  import.meta.env.VITE_FEATURE_TENANT_THEMING === "true";

// ─────────────────────────────────────────────
// Storage keys
// ─────────────────────────────────────────────

const STORAGE_THEME = "ac_theme";
const STORAGE_DENSITY = "ac_density";

// ─────────────────────────────────────────────
// Context shape
// ─────────────────────────────────────────────

interface ThemeContextValue {
  theme: ThemeName;
  density: DensityName;
  setTheme: (name: ThemeName) => void;
  setDensity: (name: DensityName) => void;
  /** All available palette names */
  availableThemes: ThemeName[];
  /** All available density names */
  availableDensities: DensityName[];
  /** Apply tenant-level color overrides at runtime (called by TenantThemeLoader) */
  setTenantOverrides: (overrides: TenantThemeOverride | null) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

function isThemeName(v: unknown): v is ThemeName {
  return (
    v === "calm" || v === "clinical" || v === "editorial" || v === "operator"
  );
}

function isDensityName(v: unknown): v is DensityName {
  return v === "compact" || v === "medium" || v === "comfortable";
}

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage unavailable (e.g. private mode) — silent fail
  }
}

function applyTokensToRoot(
  themeName: ThemeName,
  densityName: DensityName,
  tenantOverrides?: TenantThemeOverride,
): void {
  const root = document.documentElement;
  let tokens = themes[themeName];

  if (TENANT_THEMING_ENABLED && tenantOverrides) {
    // Sanitize each override value before merging — reject anything not a hex color
    const safe: TenantThemeOverride = {};
    for (const key of Object.keys(
      tenantOverrides,
    ) as (keyof TenantThemeOverride)[]) {
      const raw = tenantOverrides[key];
      if (raw !== undefined) {
        const sanitized = sanitizeCssColor(raw);
        if (sanitized !== null) {
          (safe as Record<string, string>)[key] = sanitized;
        }
      }
    }
    tokens = mergeTokenOverrides(tokens, safe);
  }

  const vars = buildCssVars(tokens, densityName);

  // Apply each CSS variable
  for (const [prop, value] of Object.entries(vars)) {
    root.style.setProperty(prop, value);
  }

  // Also set the data-theme attribute so tokens.css [data-theme=...] selectors
  // remain compatible during the migration period
  root.setAttribute("data-theme", themeName);
  root.setAttribute(
    "data-density",
    densityName === "medium" ? "" : densityName,
  );
}

// ─────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────

interface AppThemeProviderProps {
  children: React.ReactNode;
  /** Override persisted preference (useful in tests / Storybook) */
  defaultTheme?: ThemeName;
  defaultDensity?: DensityName;
  /** Initial tenant overrides (useful in tests / Storybook — runtime use setTenantOverrides) */
  initialTenantOverrides?: TenantThemeOverride;
}

export function AppThemeProvider({
  children,
  defaultTheme = "clinical",
  defaultDensity = "medium",
  initialTenantOverrides,
}: AppThemeProviderProps) {
  const [theme, setThemeState] = useState<ThemeName>(() => {
    const stored = readStorage(STORAGE_THEME);
    return isThemeName(stored) ? stored : defaultTheme;
  });

  const [density, setDensityState] = useState<DensityName>(() => {
    const stored = readStorage(STORAGE_DENSITY);
    return isDensityName(stored) ? stored : defaultDensity;
  });

  const [tenantOverrides, setTenantOverridesState] = useState<
    TenantThemeOverride | null | undefined
  >(initialTenantOverrides);

  // Apply tokens whenever theme, density, or tenant overrides change
  useEffect(() => {
    applyTokensToRoot(theme, density, tenantOverrides ?? undefined);
  }, [theme, density, tenantOverrides]);

  const setTheme = useCallback((name: ThemeName) => {
    writeStorage(STORAGE_THEME, name);
    setThemeState(name);
  }, []);

  const setDensity = useCallback((name: DensityName) => {
    writeStorage(STORAGE_DENSITY, name);
    setDensityState(name);
  }, []);

  const setTenantOverrides = useCallback(
    (overrides: TenantThemeOverride | null) => {
      setTenantOverridesState(overrides);
    },
    [],
  );

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      density,
      setTheme,
      setDensity,
      setTenantOverrides,
      availableThemes: ["calm", "clinical", "editorial", "operator"],
      availableDensities: ["compact", "medium", "comfortable"],
    }),
    [theme, density, setTheme, setDensity, setTenantOverrides],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

// ─────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────

export function useAppTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useAppTheme must be used within AppThemeProvider");
  }
  return ctx;
}

export function useAppThemeOptional(): ThemeContextValue | null {
  return useContext(ThemeContext);
}

// Re-export token types for convenience
export type { ThemeName, DensityName, DensityTokens } from "../theme/tokens";
export { themes, densityPresets };
